import type { User } from '@/entities/auth/user.entity';
import type { JWTUser } from '@/entities/auth/jwt-user.entity';
import type { JwtAnonymous } from '@/entities/auth/jwt-anonymous.entity';
import type {
  CreateDeviceAuthCodeDto,
  CreateDeviceAuthCodeResponseDto,
  JwtDeviceDto,
  JwtExternalDriverDto,
} from '@carvia/ros-client-types';
import type { TokenPayload } from '@/entities/auth/jwt-user.entity';
import type { AxiosAuthRefreshRequestConfig } from 'axios-auth-refresh';
import { Service } from './http-service';
import { API_URL } from '@/constants/api-url.constant';
import type { RawRule } from '@casl/ability';
import { ability } from '@/main.ts';

class AuthService extends Service {
  constructor() {
    super('/auth');
  }

  async login(username: string, password: string) {
    await this.httpClient.post(
      `${this.endpoint}/login`,
      {
        username,
        password,
      },
      {
        headers: {
          Authorization: null,
        },
      },
    );
  }

  async exchangeDeviceAuthCode(code: string): Promise<void> {
    await this.httpClient.post(
      `${this.endpoint}/device/exchange`,
      { code: code.trim() },
      {
        headers: {
          Authorization: null,
        },
      },
    );
  }

  async logout() {
    await this.httpClient.post(`${this.endpoint}/logout`);
  }

  async refreshAccessToken() {
    await this.httpClient.post(`${API_URL.value}${this.endpoint}/refresh`, undefined, {
      skipAuthRefresh: true,
    } as AxiosAuthRefreshRequestConfig);
  }

  async getMe(): Promise<User> {
    const res = await this.httpClient.get<User>(`${this.endpoint}/me`);
    return res.data;
  }

  async getAbilities(): Promise<RawRule[]> {
    const res = await this.httpClient.get<RawRule[]>(`${this.endpoint}/abilities`);
    ability.update([]);
    if (res.data) {
      ability.update(res.data as any);
    }
    return res.data;
  }

  async getTokenPayload(): Promise<TokenPayload> {
    const res = await this.httpClient.get<
      JWTUser | JwtAnonymous | JwtDeviceDto | JwtExternalDriverDto
    >(`${this.endpoint}/token-payload?requireUser=true`);
    if (res.data.id === null) {
      throw new Error('Token payload does not include user');
    }
    return res.data;
  }

  async createDeviceAuthCode(
    payload: CreateDeviceAuthCodeDto,
  ): Promise<CreateDeviceAuthCodeResponseDto> {
    const res = await this.httpClient.post<CreateDeviceAuthCodeResponseDto>(
      `${this.endpoint}/device/code`,
      payload,
    );
    return res.data;
  }

  async sendPasswordResetEmail(email: string) {
    const res = await this.httpClient.post(`${this.endpoint}/send-password-reset-mail`, { email });
    return res.data;
  }

  async resetPassword(token: string, password: string) {
    await this.httpClient.post(`${this.endpoint}/reset-password`, {
      token,
      password,
    });
  }

  async startGoogleLogin() {
    window.location.href = `${API_URL.value}${this.endpoint}/google/login`;
  }

  async oauthCallback(queryString: string) {
    await this.httpClient.get(`${this.endpoint}/google/verify${queryString}`);
  }
}

export const authService = new AuthService();
